import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Checkbox, Collapse, IconButton, TableBody, TableCell, TableRow, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import UPButton from 'components/button';
import { IApiError } from 'types/errors';
import { EventBusinessObject, EventBusinessObjectLabels } from 'types/errors/ApiErrorBusinessObjects';
import { useNavigate } from 'react-router-dom';
import { DetailsTable, StyledTableCell, StyledTableRow, TableExpansionContainer } from './styles';

const ApiErrorRow = (props: {
  row: IApiError;
  isChecked: boolean;
  handleClick: (event: any, id: number) => void;
  resetOpenKey: number;
}): JSX.Element => {
  const { row, isChecked, handleClick, resetOpenKey } = props;
  const [open, setOpen] = useState<boolean>(false);

  const navigate = useNavigate();

  useEffect(() => {
    setOpen(false);
  }, [resetOpenKey]);

  const formatDate = (): string => {
    const tzoffset = new Date().getTimezoneOffset() * 60000; // offset in milliseconds
    const withOffSet = new Date(row.errorDate).getTime() - tzoffset;
    const d = new Date(withOffSet).toISOString().split('T');
    // const d = new Date(row.errorDate).toISOString().split('T');
    return `${d[0]}\n${d[1].split('.')[0]}`;
  };

  const isCallToActionVisible = (): boolean => {
    if (!row || !row.source?.toLowerCase()?.includes('uportal')) {
      return false;
    }

    let requestData;
    try {
      requestData = JSON.parse(row.requestData);
      if (!requestData) return false;
    } catch {
      return false;
    }

    const typeLower = row.type?.toLowerCase() || '';
    const businessObject = row.identifier?.businessObject;

    const isServiceConfirmation =
      typeLower.includes('create') &&
      businessObject === EventBusinessObject.Service &&
      !!requestData.contractHourConfirmation?.contractDateId;

    const isContractEdit =
      typeLower.includes('update') && businessObject === EventBusinessObject.Contract && !!requestData.contractId;

    const isEmployeeEdit =
      typeLower.includes('update') && businessObject === EventBusinessObject.Employee && !!requestData.employeeId;

    return isServiceConfirmation || isContractEdit || isEmployeeEdit;
  };

  const onCallToActionClick = (): void => {
    if (!isCallToActionVisible()) return;
    const requestData = JSON.parse(row.requestData);
    const { contractId, employeeId } = requestData;
    const { companyId } = row.identifier;
    if (row.identifier.businessObject === EventBusinessObject.Contract) {
      navigate(`/contracts/edit/${companyId}/${contractId}`);
      return;
    }
    if (row.identifier.businessObject === EventBusinessObject.Employee) {
      navigate(`/employees/edit/${companyId}/${employeeId}`);
      return;
    }
    if (row.identifier.businessObject === EventBusinessObject.Service) {
      const contractDateId = requestData?.contractHourConfirmation?.contractDateId;
      navigate(`/contracts/confirm/${companyId}/${contractDateId}`);
    }
  };

  return (
    <>
      <StyledTableRow sx={{ '& > *': { borderBottom: 'unset' } }} aria-checked={isChecked}>
        <StyledTableCell padding="checkbox" align="left">
          {!row.archived && (
            <Checkbox
              color="primary"
              checked={isChecked}
              onChange={event => handleClick(event, row.id)}
              inputProps={{
                'aria-labelledby': `error-${row.id}`,
              }}
            />
          )}
        </StyledTableCell>
        <StyledTableCell width="3%" align="left">
          <IconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </StyledTableCell>
        <StyledTableCell width="10%" align="center" sx={{ whiteSpace: 'pre-line' }}>
          {formatDate()}
        </StyledTableCell>
        <StyledTableCell align="left">{EventBusinessObjectLabels[row.identifier.businessObject]}</StyledTableCell>
        <StyledTableCell align="left" style={{ fontWeight: 'bold' }}>
          {row.type}
        </StyledTableCell>
        <StyledTableCell align="left" style={{ fontWeight: 'bold' }}>
          {row.source}
        </StyledTableCell>
        <StyledTableCell align="left">{row.identifier.companyName}</StyledTableCell>
        <StyledTableCell align="left" style={{ fontWeight: 'bold' }}>
          {row.identifier.title}
        </StyledTableCell>
        <StyledTableCell align="center">
          {isCallToActionVisible() && <UPButton outlined onClick={onCallToActionClick} text="CTA" />}
        </StyledTableCell>
      </StyledTableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <TableExpansionContainer>
              <Typography gutterBottom component="div">
                <strong>{row.identifier.errorMessage}</strong>
              </Typography>
              <DetailsTable>
                <TableBody>
                  <TableRow>
                    <strong>Request Details:</strong>
                  </TableRow>
                  <TableRow>{row.requestData}</TableRow>
                  <TableRow>
                    <strong>Response Details:</strong>
                  </TableRow>
                  <TableRow>{row.message}</TableRow>
                  {row.identifier.eventContextJson && (
                    <>
                      <TableRow>
                        <strong>Event Context:</strong>
                      </TableRow>
                      <TableRow>{row.identifier.eventContextJson}</TableRow>
                    </>
                  )}
                </TableBody>
              </DetailsTable>
            </TableExpansionContainer>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

export default ApiErrorRow;
